import type {
  ClosureReason,
  Pool,
  PoolSection,
  PoolStatus,
  Report,
  ReportRequest,
  ReportResponse,
  ReopenEstimate,
} from "../shared/types";
import { CITY } from "./city";

// Set VITE_API_BASE in .env to point at a real API Gateway stage.
// Empty string => same-origin "/api" (CloudFront can route /api to the HTTP API).
const API_BASE = import.meta.env.VITE_API_BASE ?? "";

// Namespaced per city so the DC and Arlington sites keep separate device ids.
const DEVICE_KEY = `${CITY.localStoragePrefix}:deviceId`;

/** A stable per-browser id used for report rate limiting (no login, no PII). */
export function getDeviceId(): string {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) throw new Error(`GET ${path} failed: ${res.status}`);
  return res.json() as Promise<T>;
}

export function listPools(): Promise<Pool[]> {
  return getJSON<Pool[]>("/api/pools");
}

export function getPoolStatus(poolId: string): Promise<PoolStatus> {
  return getJSON<PoolStatus>(`/api/pools/${encodeURIComponent(poolId)}/status`);
}

/** Batch status for every visible pool in one round-trip (homepage warm-up). */
export function getAllStatuses(): Promise<PoolStatus[]> {
  return getJSON<PoolStatus[]>("/api/pools/status");
}

export interface RecentReports {
  poolId: string;
  section: PoolSection;
  /** Newest first, over the last 24h. */
  reports: Report[];
}

/** Recent crowd reports for one section (last 24h, newest first). */
export function getRecentReports(
  poolId: string,
  section: PoolSection,
): Promise<RecentReports> {
  return getJSON<RecentReports>(
    `/api/pools/${encodeURIComponent(poolId)}/reports?section=${encodeURIComponent(section)}`,
  );
}

export interface SubmitResult {
  ok: boolean;
  /** Seconds until this device may report again for this pool+section. */
  retryAfter?: number;
  status?: PoolStatus;
  /**
   * The `ts` of the row just written (first-click WRITE only). The caller stashes
   * it and passes it back as `targetTs` to `enrichClosedReport` so the follow-up
   * reason/reopen annotation lands on the SAME row instead of a second report.
   */
  reportTs?: number;
}

export async function submitReport(
  // `reason` rides along on `req` (it's an optional field of ReportRequest) and
  // is JSON-stringified through; undefined reasons serialize to nothing.
  req: Omit<ReportRequest, "deviceId">,
): Promise<SubmitResult> {
  const res = await fetch(`${API_BASE}/api/reports`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...req, deviceId: getDeviceId() } satisfies ReportRequest),
  });
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get("Retry-After") ?? "0");
    return { ok: false, retryAfter };
  }
  if (!res.ok) throw new Error(`POST /api/reports failed: ${res.status}`);
  const body = (await res.json()) as ReportResponse;
  return { ok: true, status: body, reportTs: body.reportTs };
}

/**
 * Enrich an already-committed CLOSED report in place: POST `{ ..., status:
 * "closed", targetTs }` so the server annotates the row at `targetTs` with the
 * chosen `reason`/`reopenEstimate` instead of writing a new report (no rollup).
 * Used by the closed flow after the first-click report has landed.
 */
export async function enrichClosedReport(args: {
  poolId: string;
  section: PoolSection;
  targetTs: number;
  reason?: ClosureReason;
  reopenEstimate?: ReopenEstimate;
}): Promise<SubmitResult> {
  const res = await fetch(`${API_BASE}/api/reports`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...args,
      status: "closed",
      deviceId: getDeviceId(),
    } satisfies ReportRequest),
  });
  // The server exempts enrichments from the limiter, but stay defensive: a 429
  // just means the annotation didn't land — the closed STATUS already did.
  if (res.status === 429) {
    const retryAfter = Number(res.headers.get("Retry-After") ?? "0");
    return { ok: false, retryAfter };
  }
  if (!res.ok) throw new Error(`POST /api/reports (enrich) failed: ${res.status}`);
  return { ok: true, status: (await res.json()) as PoolStatus };
}
